import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, ImagePlus, Pencil, Check, Star, X } from "lucide-react";
import AdminNav from "@/components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "od_admin_token";

export default function AdminGallery() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [featured, setFeatured] = useState(null);
  const [featModal, setFeatModal] = useState(null); // photo being featured
  const [featForm, setFeatForm] = useState({ title: "", subtitle: "", quote: "" });
  const fileRef = useRef(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadGallery = async () => {
    setLoadingList(true);
    try {
      const [list, feat] = await Promise.all([
        axios.get(`${API}/gallery`),
        axios.get(`${API}/featured`).catch(() => ({ data: null })),
      ]);
      setItems(list.data);
      setFeatured(feat.data || null);
    } catch {
      toast.error("Failed to load gallery");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (token) loadGallery();
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Welcome back");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setItems([]);
  };

  const handleFiles = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    setUploading(true);
    let okCount = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label || "");
      try {
        await axios.post(`${API}/gallery/upload`, fd, {
          headers: { ...auth.headers, "Content-Type": "multipart/form-data" },
        });
        okCount += 1;
      } catch (err) {
        toast.error(
          err?.response?.data?.detail || `Failed to upload ${file.name}`
        );
      }
    }
    setUploading(false);
    setLabel("");
    if (fileRef.current) fileRef.current.value = "";
    if (okCount) {
      toast.success(`Uploaded ${okCount} photo${okCount > 1 ? "s" : ""}`);
      loadGallery();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, auth);
      toast.success("Photo deleted");
      setItems((it) => it.filter((p) => p.id !== id));
    } catch {
      toast.error("Failed to delete");
    }
  };

  const saveLabel = async (id) => {
    try {
      await axios.patch(`${API}/gallery/${id}`, { label: editLabel }, auth);
      setItems((it) => it.map((p) => (p.id === id ? { ...p, label: editLabel } : p)));
      setEditingId(null);
      toast.success("Caption updated");
    } catch {
      toast.error("Failed to update caption");
    }
  };

  const openFeatureModal = (photo) => {
    setFeatModal(photo);
    setFeatForm({
      title: photo.featured_title || "",
      subtitle: photo.featured_subtitle || photo.label || "",
      quote: photo.featured_quote || "",
    });
  };

  const submitFeature = async () => {
    if (!featModal) return;
    try {
      await axios.post(`${API}/gallery/${featModal.id}/featured`, featForm, auth);
      toast.success("Set as Car of the Week");
      setFeatModal(null);
      loadGallery();
    } catch {
      toast.error("Failed to set featured");
    }
  };

  const clearFeatured = async () => {
    if (!window.confirm("Remove the current Car of the Week?")) return;
    try {
      await axios.delete(`${API}/featured`, auth);
      toast.success("Featured cleared");
      setFeatured(null);
      setItems((it) => it.map((p) => ({ ...p, is_featured: false })));
    } catch {
      toast.error("Failed to clear");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md p-10 bg-[#111] border border-[#262626] rounded-sm"
        >
          <div className="text-xs uppercase tracking-[0.3em] text-[#D6B98D] mb-3">
            Overdrive · Admin
          </div>
          <h1 className="font-serif text-4xl text-white font-light mb-8 tracking-tight">
            Gallery Manager
          </h1>
          <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full bg-transparent border border-[#262626] focus:border-[#D6B98D] focus:ring-1 focus:ring-[#D6B98D]/40 rounded-sm px-4 py-3.5 text-sm text-white outline-none transition-colors"
            placeholder="Enter admin password"
            autoFocus
          />
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-button"
            className="w-full mt-6 inline-flex items-center justify-center gap-2 py-4 bg-[#D6B98D] text-[#050505] hover:bg-[#E5CCA5] disabled:opacity-60 transition-all duration-300 text-xs uppercase tracking-[0.3em] font-bold rounded-sm"
          >
            {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <AdminNav onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Upload Card */}
        <section className="mb-12 p-8 lg:p-10 bg-[#111] border border-[#262626] rounded-sm">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D6B98D] mb-3">
            Add new photos
          </div>
          <h2 className="font-serif text-3xl text-white font-light mb-6 tracking-tight">
            Upload from your computer or phone
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
                Caption (optional, applied to all in this batch)
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="upload-label-input"
                placeholder="e.g. 2021 BMW M3 · Premium detail"
                className="w-full bg-transparent border border-[#262626] focus:border-[#D6B98D] focus:ring-1 focus:ring-[#D6B98D]/40 rounded-sm px-4 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-colors"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-button"
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-[#D6B98D] text-[#050505] hover:bg-[#E5CCA5] disabled:opacity-60 transition-all duration-300 text-xs uppercase tracking-[0.25em] font-bold rounded-sm"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" /> Choose photos
                  </>
                )}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                data-testid="upload-file-input"
              />
            </div>
          </div>
          <p className="mt-4 text-[11px] text-white/40">
            JPG, PNG, WEBP, or GIF · Max 15MB per file · You can select multiple at once.
          </p>
        </section>

        {/* Featured banner */}
        {featured && (
          <section
            data-testid="featured-banner"
            className="mb-10 p-6 bg-[#0c0c0c] border border-[#D6B98D]/40 rounded-sm flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
          >
            <div className="flex items-center gap-4">
              <img
                src={`${API}/gallery/file/${featured.id}`}
                alt="Featured"
                className="w-20 h-20 object-cover rounded-sm border border-[#D6B98D]/30"
              />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-1">
                  Current Car of the Week
                </div>
                <div className="font-serif text-lg text-white">
                  {featured.featured_title || "(untitled)"}
                </div>
                <div className="text-xs text-white/40">
                  {featured.featured_subtitle || featured.label || "—"}
                </div>
              </div>
            </div>
            <button
              onClick={clearFeatured}
              data-testid="clear-featured-button"
              className="text-[11px] uppercase tracking-[0.25em] text-white/50 hover:text-red-400 transition-colors"
            >
              Remove
            </button>
          </section>
        )}

        {/* Gallery list */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
                Current gallery
              </div>
              <h2 className="font-serif text-3xl text-white font-light tracking-tight">
                {items.length} photo{items.length === 1 ? "" : "s"}
              </h2>
            </div>
          </div>

          {loadingList ? (
            <div className="py-20 flex items-center justify-center text-white/50">
              <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div
              data-testid="empty-gallery"
              className="py-20 px-6 text-center border border-dashed border-[#262626] rounded-sm"
            >
              <ImagePlus
                className="w-10 h-10 text-[#D6B98D] mx-auto mb-4"
                strokeWidth={1}
              />
              <p className="text-white/60 text-sm">
                Your gallery is empty. Upload your first photos above.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((p) => (
                <div
                  key={p.id}
                  data-testid={`gallery-admin-item-${p.id}`}
                  className="group relative bg-[#111] border border-[#262626] hover:border-[#D6B98D]/40 transition-all duration-300 rounded-sm overflow-hidden"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#0a0a0a]">
                    <img
                      src={`${API}/gallery/file/${p.id}`}
                      alt={p.label || "Gallery photo"}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    {editingId === p.id ? (
                      <div className="flex gap-2">
                        <input
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          className="flex-1 bg-transparent border border-[#262626] focus:border-[#D6B98D] rounded-sm px-3 py-2 text-xs text-white outline-none"
                          autoFocus
                        />
                        <button
                          onClick={() => saveLabel(p.id)}
                          data-testid={`save-label-${p.id}`}
                          className="px-3 py-2 bg-[#D6B98D] text-[#050505] rounded-sm hover:bg-[#E5CCA5] transition-colors"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-white/70 truncate">
                          {p.label || <em className="text-white/30">No caption</em>}
                        </span>
                        <button
                          onClick={() => {
                            setEditingId(p.id);
                            setEditLabel(p.label || "");
                          }}
                          data-testid={`edit-label-${p.id}`}
                          className="text-white/40 hover:text-[#D6B98D] transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(p.id)}
                    data-testid={`delete-photo-${p.id}`}
                    className="absolute top-2 right-2 p-2 bg-[#050505]/80 backdrop-blur border border-[#262626] text-red-400 hover:border-red-400 hover:bg-red-500/20 transition-all rounded-sm opacity-0 group-hover:opacity-100"
                    title="Delete photo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openFeatureModal(p)}
                    data-testid={`feature-photo-${p.id}`}
                    className={`absolute top-2 left-2 p-2 backdrop-blur border transition-all rounded-sm ${
                      p.is_featured
                        ? "bg-[#D6B98D] text-[#050505] border-[#D6B98D] opacity-100"
                        : "bg-[#050505]/80 border-[#262626] text-[#D6B98D] hover:border-[#D6B98D] opacity-0 group-hover:opacity-100"
                    }`}
                    title={p.is_featured ? "Currently featured" : "Set as Car of the Week"}
                  >
                    <Star className="w-4 h-4" fill={p.is_featured ? "#050505" : "none"} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Feature modal */}
      {featModal && (
        <div
          data-testid="feature-modal"
          className="fixed inset-0 z-50 bg-[#050505]/80 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setFeatModal(null)}
        >
          <div
            className="w-full max-w-lg bg-[#0c0c0c] border border-[#D6B98D]/30 rounded-sm p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D6B98D]">
                  Car of the Week
                </div>
                <h3 className="font-serif text-2xl text-white mt-1">Set featured details</h3>
              </div>
              <button
                onClick={() => setFeatModal(null)}
                data-testid="close-feature-modal"
                className="p-2 text-white/40 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <img
              src={`${API}/gallery/file/${featModal.id}`}
              alt=""
              className="w-full aspect-[16/9] object-cover rounded-sm border border-[#262626] my-5"
            />

            <div className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
                  Headline
                </label>
                <input
                  value={featForm.title}
                  onChange={(e) => setFeatForm({ ...featForm, title: e.target.value })}
                  data-testid="feature-title-input"
                  placeholder="e.g. 2021 BMW M3 Competition"
                  className="w-full bg-transparent border border-[#262626] focus:border-[#D6B98D] rounded-sm px-4 py-3 text-sm text-white outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
                  Subtitle (e.g. package or service)
                </label>
                <input
                  value={featForm.subtitle}
                  onChange={(e) => setFeatForm({ ...featForm, subtitle: e.target.value })}
                  data-testid="feature-subtitle-input"
                  placeholder="e.g. Premium Package · 4 hrs"
                  className="w-full bg-transparent border border-[#262626] focus:border-[#D6B98D] rounded-sm px-4 py-3 text-sm text-white outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
                  Quote / note (optional)
                </label>
                <textarea
                  value={featForm.quote}
                  onChange={(e) => setFeatForm({ ...featForm, quote: e.target.value })}
                  data-testid="feature-quote-input"
                  rows={3}
                  placeholder="A short note about the car or the customer."
                  className="w-full bg-transparent border border-[#262626] focus:border-[#D6B98D] rounded-sm px-4 py-3 text-sm text-white outline-none resize-none"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setFeatModal(null)}
                className="flex-1 py-3 border border-[#262626] hover:border-white/40 text-white/70 text-xs uppercase tracking-[0.25em] rounded-sm transition-all"
              >
                Cancel
              </button>
              <button
                onClick={submitFeature}
                data-testid="confirm-feature-button"
                className="flex-1 py-3 bg-[#D6B98D] text-[#050505] hover:bg-[#E5CCA5] text-xs uppercase tracking-[0.25em] font-bold rounded-sm transition-all"
              >
                Set as Featured
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
